// Pure helpers of the Applications page: what the card shows for a state,
// which modal its button opens, and client-side form validation. The server
// derives the state (backend services/appIntegrationService.js) and repeats
// every validation; nothing here is a security boundary.

import { formatSar } from "../../../utils/billingMoney";

export const STATE_TAG = {
  notActivated: "neutral",
  subscriptionPending: "warning",
  subscriptionSent: "info",
  subscriptionCompleted: "primary",
  integrationInProgress: "warning",
  integrationRejected: "danger",
  active: "success",
  activeNoBranches: "warning",
  closed: "danger",
};

export const REQUEST_TAG = {
  pending: "warning",
  sending: "info",
  sent: "info",
  failed: "danger",
  completed: "success",
  cancelled: "neutral",
};

export const BRANCH_TAG = { active: "success", pending: "warning", inactive: "neutral", unconfigured: "neutral" };

// Main button of a card: { label key of t.action, open: modal to open, variant }.
export function cardAction(state, { canManage, canReview }) {
  if (!canManage && !canReview) return { label: "view", open: "manage", variant: "neutral" };
  switch (state) {
    case "notActivated":
      return canManage ? { label: "activate", open: "activate", variant: "primary" } : { label: "view", open: "manage", variant: "neutral" };
    case "subscriptionPending":
      return { label: "manageRequest", open: "manage", variant: "warning" };
    case "subscriptionSent":
      return { label: "viewRequest", open: "manage", variant: "primary" };
    case "subscriptionCompleted":
      return canManage ? { label: "completeIntegration", open: "integrate", variant: "primary" } : { label: "viewRequest", open: "manage", variant: "primary" };
    case "integrationInProgress":
    case "integrationRejected":
      return { label: "viewIntegration", open: "manage", variant: "primary" };
    case "closed":
      return canReview ? { label: "reactivate", open: "manage", variant: "success" } : { label: "manage", open: "manage", variant: "neutral" };
    default:
      return { label: "manage", open: "manage", variant: "primary" };
  }
}

// Branch ids / names are all the server returns of a revenue; format it with
// the existing SAR formatter (it takes minor units).
export const formatRevenue = (revenue, lang) => (revenue ? formatSar(Math.round(Number(revenue.amount) * 100), lang) : "");

const PHONE = /^\+?[0-9\s\-()]{7,20}$/;
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());

const blank = (value) => !String(value || "").trim();

export function validateAccount(account = {}, { hasPassword = false } = {}) {
  const errors = {};
  if (blank(account.id)) errors["account.id"] = "required";
  if (blank(account.email)) errors["account.email"] = "required";
  else if (!isEmail(account.email)) errors["account.email"] = "invalid";
  if (blank(account.password) && !hasPassword) errors["account.password"] = "required";
  return errors;
}

export function validateManager(manager = {}) {
  const errors = {};
  if (blank(manager.name)) errors["manager.name"] = "required";
  if (blank(manager.phone)) errors["manager.phone"] = "required";
  else if (!PHONE.test(String(manager.phone).trim())) errors["manager.phone"] = "invalid";
  if (blank(manager.email)) errors["manager.email"] = "required";
  else if (!isEmail(manager.email)) errors["manager.email"] = "invalid";
  return errors;
}

// branches: { [branchId]: { selected, appId, appName } }. Every selected branch
// needs its own id and name; ids are never shared between branches.
export function validateBranches(branches = {}, { requireSelection = true } = {}) {
  const errors = {};
  const selected = Object.entries(branches).filter(([, branch]) => branch.selected);
  if (!selected.length && requireSelection) errors.branches = "required";
  const seen = new Map();
  selected.forEach(([branchId, branch]) => {
    if (blank(branch.appId)) errors[`branches.${branchId}.appId`] = "required";
    if (blank(branch.appName)) errors[`branches.${branchId}.appName`] = "required";
    const folded = String(branch.appId || "").trim().toLowerCase();
    if (folded) {
      if (seen.has(folded)) {
        errors[`branches.${branchId}.appId`] = "duplicate";
        errors[`branches.${seen.get(folded)}.appId`] = "duplicate";
      } else seen.set(folded, branchId);
    }
  });
  return errors;
}

// values: { account: {id,email,password}, manager: {name,phone,email}, branches: { [branchId]: { selected, appId, appName } } }
// Returns { [fieldPath]: "required" | "invalid" | "duplicate" } (empty when valid).
export function validateIntegration(values, { hasPassword = false } = {}) {
  return { ...validateAccount(values.account, { hasPassword }), ...validateManager(values.manager), ...validateBranches(values.branches) };
}

// Reason for a deactivation. Same rule as the backend (appChangeRequestService):
// not blank, 5 to 1000 characters, at least one letter or digit.
export const REASON_MIN = 5;
export function validateReason(value) {
  const reason = String(value || "").trim();
  if (!reason) return "required";
  if (reason.length < REASON_MIN || reason.length > 1000 || !/[\p{L}\p{N}]/u.test(reason)) return "invalid";
  return "";
}

export const REQUEST_TYPE_TAG = { integration: "primary", activation: "success", deactivation: "danger", mapping: "info" };
export const CHANGE_STATUS_TAG = { inprogress: "warning", approved: "success", rejected: "danger", cancelled: "neutral" };

// Maps server field paths ("branches.2.appId", index based) to the branch ids
// the form is keyed by, so server validation lands on the right input.
export function mapServerFields(fields = {}, orderedBranchIds = []) {
  const mapped = {};
  Object.entries(fields).forEach(([path, code]) => {
    const match = /^branches\.(\d+)\.(\w+)$/.exec(path);
    const key = match ? `branches.${orderedBranchIds[Number(match[1])] ?? match[1]}.${match[2]}` : path;
    mapped[key] = String(code).toLowerCase();
  });
  return mapped;
}

export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
